import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch } from '../../utils/Utils.js';
import './NivoChartCard.css';

// "Emphasis" form, not categorical: these bars are one ranked comparison
// (which bull/breed performs best), not distinct series that all need their
// own identity color — a color per bar would just repeat what the axis
// label and sort order already say. The one that matters gets the accent;
// the rest recede. Both are documented tokens (palette.md slot-1 blue /
// baseline-axis gray), not hand-picked.
const ACCENT = '#2a78d6';
const DEEMPHASIS = '#c3c2b7';

const BullSuccessRate = () => {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState('bull'); // 'bull' | 'breed'
  const [bars, setBars] = useState(null);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    let results = null;
    let bulls = null;

    const tryBuild = () => {
      if (!results || !bulls) return;

      const bullMap = {};
      bulls.forEach(b => {
        const name = b.tag ? `${b.name || '—'} (#${b.tag})` : (b.name || '—');
        bullMap[b._id] = { name, breed: b.breed || t('unknown') };
      });

      const agg = {};
      results.forEach(res => {
        const bullId = res.bull_id;
        if (!bullId) return;
        const bull = bullMap[bullId];
        if (!bull) return;
        // A dam dying isn't the bull's fault — excluded from both the
        // success count and the total, not counted as a failure.
        if (res.outcome === 'dam_died') return;

        const key = groupBy === 'bull' ? bull.name : (bull.breed || t('unknown'));
        if (!agg[key]) agg[key] = { total: 0, success: 0 };
        agg[key].total++;
        if (res.outcome === 'calved_alive' || res.outcome === 'calved_stillborn') agg[key].success++;
      });

      const names = Object.keys(agg);
      if (names.length === 0) {
        setSummary(t('chart_summary_no_bull_data'));
        setBars(null);
        return;
      }

      // Nivo's horizontal bar layout draws the first array item at the
      // BOTTOM of the chart (band scale, not reading order) — sort ascending
      // so the best performer ends up last in the array, and visually on top.
      const rows = names
        .map(name => ({
          name,
          rate: Math.round((agg[name].success / agg[name].total) * 100),
          success: agg[name].success,
          total: agg[name].total,
        }))
        .sort((a, b) => a.rate - b.rate);
      rows.forEach((r, i) => { r.isBest = i === rows.length - 1; });

      const best = rows[rows.length - 1];
      setSummary(t('chart_summary_best_bull', { name: best.name, percent: best.rate }));
      setBars(rows);
    };

    Fetch('api/reproduction/results', 'get', null, (data) => { results = data || []; tryBuild(); });
    Fetch('api/data/reproduction.bulls', 'get', null, (data) => { bulls = data || []; tryBuild(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  return (
    <div className="Chart NivoCard">
      <h3>{t('bull_success_rate_chart')}</h3>
      <div className="nivo-toggle-row">
        <button
          className={`nivo-toggle-btn${groupBy === 'bull' ? ' active' : ''}`}
          onClick={() => setGroupBy('bull')}
        >
          {t('group_by_bull')}
        </button>
        <button
          className={`nivo-toggle-btn${groupBy === 'breed' ? ' active' : ''}`}
          onClick={() => setGroupBy('breed')}
        >
          {t('group_by_breed')}
        </button>
      </div>
      <p className="nivo-card-subtitle">{summary}</p>

      {!bars ? (
        <p className="nivo-card-empty">{t('chart_summary_no_bull_data')}</p>
      ) : (
        <div style={{ height: Math.max(bars.length * 44, 120) }}>
          <ResponsiveBar
            data={bars}
            keys={['rate']}
            indexBy="name"
            layout="horizontal"
            margin={{ top: 8, right: 36, bottom: 42, left: 90 }}
            valueScale={{ type: 'linear', min: 0, max: 100 }}
            padding={0.35}
            borderRadius={4}
            colors={(bar) => (bar.data.isBest ? ACCENT : DEEMPHASIS)}
            enableGridY={false}
            gridXValues={5}
            axisBottom={{ legend: t('success_rate_pct'), legendPosition: 'middle', legendOffset: 32, tickValues: 5, format: (v) => `${v}%` }}
            label={(d) => `${d.value}%`}
            labelSkipWidth={28}
            labelTextColor={(bar) => (bar.data.isBest ? '#ffffff' : '#1a1714')}
            motionConfig="gentle"
            theme={{
              axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
              grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              labels: { text: { fontWeight: 700, fontSize: 12.5 } },
            }}
            tooltip={({ data }) => (
              <div className="nivo-tooltip">
                <span className="nivo-tooltip-swatch" style={{ background: data.isBest ? ACCENT : DEEMPHASIS }} />
                <strong>{data.name}</strong>: <span className="nivo-tooltip-value">{t('chart_tooltip_success_fraction', { success: data.success, total: data.total })}</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default BullSuccessRate;
